import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { Subject, takeUntil } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { AssistanceTicketInterface } from '@app/interface/assistance-ticket';
import { AssistanceTicketApiService } from '@app/services/assistance-ticket-api.service';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Organizations } from '@app/interface/organizations';
import { OrganizationApi } from '@app/services/organization-api.service';
import { AssistanceConversationsApiService } from '@app/services/assistance-conversations-api.service';
import { AssistanceConversationsInterface } from '@app/interface/assistance-conversations';
import { AssistanceAvailabilityInterface } from '@app/interface/assistance-availability';
import { AssistanceAvailabilityApiService } from '@app/services/assistance-availability-api.service';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BookACallDialogComponent } from '@app/book-a-call-dialog/book-a-call-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'izy-commercialista-ticketing',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
    MatListModule,
    BackHeaderComponent,
    MatChipOption,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatTooltipModule,
  ],
  templateUrl: './commercialista-ticketing.component.html',
  styleUrl: './commercialista-ticketing.component.scss',
})
export class CommercialistaTicketingComponent implements OnInit {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('newConversationDialog') newConversationDialog!: TemplateRef<any>;
  @ViewChild('confirmSendDialog') confirmSendDialog!: TemplateRef<any>;
  @ViewChild('receivedMessageDialog') receivedMessageDialog!: TemplateRef<any>;

  organization_data: Organizations | null = null;

  /**
   * Stato di apertura dell'assistenza. Orari e chiusure stanno nel backend
   * (pannello admin), qui arriva solo il risultato con il messaggio già pronto.
   */
  availability: AssistanceAvailabilityInterface | null = null;

  /**
   * Usato se l'endpoint non risponde: senza fallback un errore di rete
   * lascerebbe l'avviso in chat con un messaggio vuoto.
   */
  private readonly availabilityFallback: AssistanceAvailabilityInterface = {
    is_open: true,
    reason: 'OPEN',
    message: 'Ti risponderemo entro 48 ore lavorative.',
    closure_reason: '',
    schedule: {},
  };

  tickets: AssistanceTicketInterface[] = [];
  conversations: AssistanceConversationsInterface[] = [];
  selectedConversation: AssistanceConversationsInterface | null = null;

  unreadCounts: { [conversationId: number]: number } = {};

  loading = true;
  error: string | null = null;
  // private destroy$ = new Subject<void>();
  toReadTickets: number = 0;
  newMessage: string = '';

  // for new conversations
  newDialogSubject: string = '';
  newDialogTopic: string = 'COMMERCIALISTA'; // Default selection

  // Maps the backend ENUM values to nice labels
  conversationTopics = [
    { value: 'TECHNICAL', label: 'Assistenza tecnica' },
    { value: 'COMMERCIALISTA', label: 'Commercialista' },
    { value: 'APP', label: "Info sull'app e funzioni" },
    { value: 'OTHER', label: 'Altro tema generico' },
  ];

  // Filter states
  // activeFilters = {
  //   withAttachment: false,
  //   unread: false,
  // };

  constructor(
    private ticketApiService: AssistanceTicketApiService,
    private conversationApiService: AssistanceConversationsApiService,
    private availabilityApiService: AssistanceAvailabilityApiService,
    private organizationService: OrganizationApi,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    // private bottomSheet: MatBottomSheet
  ) {}

  ngOnInit(): void {
    // this.loadTickets();   / now it depends on selectedConversation
    this.getOrganization();
    this.loadConversations();
    this.loadAvailability();

    this.cdr.detectChanges();
  }

  /**
   * Carica lo stato di apertura dell'assistenza. `onDone` viene invocato in
   * entrambi gli esiti, così chi deve mostrare un messaggio subito dopo non
   * resta bloccato se l'endpoint fallisce.
   */
  private loadAvailability(onDone?: () => void): void {
    this.availabilityApiService.getAvailability().subscribe({
      next: (availability: AssistanceAvailabilityInterface) => {
        this.availability = availability;
        this.cdr.detectChanges();
        onDone?.();
      },
      error: (err: any) => {
        console.error('Error loading assistance availability:', err);
        // Nessuno snackbar: è un dato accessorio, non vale un errore a schermo.
        // Si tiene l'ultimo valore buono, o il fallback se non ne abbiamo mai avuto uno.
        if (!this.availability) {
          this.availability = this.availabilityFallback;
        }
        this.cdr.detectChanges();
        onDone?.();
      },
    });
  }

  /** Unico punto da cui tutti e tre i posti nel template leggono il messaggio. */
  get availabilityMessage(): string {
    return (this.availability ?? this.availabilityFallback).message;
  }

  get isAssistanceOpen(): boolean {
    return (this.availability ?? this.availabilityFallback).is_open;
  }

  /**
   * L'avviso serve finché nessuno ha risposto: se l'ultimo messaggio della
   * conversazione è del cliente ('OUT'), la richiesta è ancora in attesa.
   */
  get showAssistanceNotice(): boolean {
    const lastTicket = this.tickets[this.tickets.length - 1];
    return !!lastTicket && lastTicket.type === 'OUT';
  }

  private getOrganization(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (organization_id && !isNaN(organization_id)) {
      const filterParams = {
        organization_id: organization_id,
      };

      this.organizationService
        .getOrganization<Organizations>(organization_id, filterParams)
        .subscribe({
          next: (organization_data: Organizations) => {
            this.organization_data = organization_data;
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.error('Error loading organization:', err);
            this.showErrorMessage("Errore nel caricamento dell'organizzazione");
            this.cdr.detectChanges();
          },
        });
    } else {
      console.error(
        'Could not extract valid organization ID from URL or route parameters',
      );
    }
  }

  selectConversation(conversation: AssistanceConversationsInterface): void {
    this.selectedConversation = conversation;
    this.loadTickets(conversation.id); // Fetch tickets for THIS conversation

    // Reset the unread count locally since they are now looking at it
    this.unreadCounts[conversation.id] = 0;
    this.loading = false;
    this.cdr.detectChanges();
  }

  getTopicLabel(topicValue: string): string {
    const topic = this.conversationTopics.find((t) => t.value === topicValue);
    // Return the nice label if found, otherwise just return the raw value as a fallback
    return topic ? topic.label : topicValue;
  }

  loadTickets(
    conversationId: number,
    sortDirection: 'asc' | 'desc' = 'asc',
  ): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.loading = true;
    this.error = null;
    this.cdr.detectChanges();

    // Prepare filter params
    const filterParams: any = {
      ordering: sortDirection === 'asc' ? 'id' : '-id',
      is_deleted: false,
      conversation: this.selectedConversation?.id, // Or conversation_id, depending on your Django filter setup
    };

    this.ticketApiService
      .getAllTickets<AssistanceTicketInterface>(organization_id, filterParams)
      .pipe()
      .subscribe({
        next: (response) => {
          // Filter and map messages
          // this.tickets = response.results.sort((a, b) => {
          //   const dateA = new Date(a.date || a.created_at || 0).getTime();
          //   const dateB = new Date(b.date || b.created_at || 0).getTime();
          //   return dateA - dateB;
          // });

          this.tickets = response.results;
          // .sort((a, b) => {
          // Sort descending: Highest ID (newest) minus Lowest ID (oldest)
          //   return a.id - b.id;
          // });

          // Loop through every ticket and apply the function
          this.tickets.forEach((ticket) => {
            // Assuming your method accepts the ticket as a parameter!
            this.toggleReadStatus(ticket);
          });

          this.loading = false;
          this.scrollToBottom();
          this.cdr.detectChanges();

          this.cdr.detectChanges(); // Force change detection after data
        },
        error: (error) => {
          this.error = 'Errore durante il caricamento';
          this.loading = false;
          console.error('Error loading tickets:', error);

          this.cdr.detectChanges(); // Force change detection after data
        },
      });

    this.cdr.detectChanges();
  }

  loadConversations(sortDirection: 'asc' | 'desc' = 'asc'): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.loading = true;
    this.error = null;
    this.cdr.detectChanges();

    // Prepare filter params
    const filterParams: any = {
      ordering: sortDirection === 'asc' ? 'id' : '-id',
    };
    filterParams.is_deleted = false;

    this.conversationApiService
      .getAllConversations<AssistanceConversationsInterface>(
        organization_id,
        filterParams,
      )
      .subscribe({
        next: (response) => {
          // this.conversations = response.results.sort((a, b) => {
          //   const dateA = new Date(a.date_start || 0).getTime();
          //   const dateB = new Date(b.date_start || 0).getTime();
          //   return dateB - dateA; // Usually you want newest conversations first
          // });

          this.conversations = response.results.sort((a, b) => {
            // Sort descending: Highest ID (newest) minus Lowest ID (oldest)
            return b.id - a.id;
          });

          this.conversations.forEach((conversation) => {
            this.fetchUnreadCount(conversation.id);
          });

          // Auto-select the first conversation if there are any
          if (this.conversations.length > 0) {
            this.selectConversation(this.conversations[0]);
          }

          this.loading = false;
          // this.cdr.detectChanges();
          this.scrollToBottom(); // We will create this method below

          this.cdr.detectChanges(); // Force change detection after data
        },
        error: (error) => {
          this.error = 'Errore durante il caricamento';
          this.loading = false;
          console.error('Error loading conversations:', error);

          this.cdr.detectChanges(); // Force change detection after data
        },
      });
  }

  fetchUnreadCount(conversationId: number): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // We only want tickets for this specific conversation
    const filterParams: any = {
      is_deleted: false,
      conversation: conversationId,
    };

    this.ticketApiService
      .getAllTickets<AssistanceTicketInterface>(organization_id, filterParams)
      .subscribe({
        next: (response) => {
          // Filter tickets that are UNREAD and came IN from the commercialista
          const unreadTickets = response.results.filter(
            (ticket) => ticket.is_read === false && ticket.type === 'IN',
          );

          // Save the count in our dictionary
          this.unreadCounts[conversationId] = unreadTickets.length;
          this.cdr.detectChanges();
        },
      });
  }

  createNewConversation(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // defaults
    this.newDialogSubject = 'Nuova Richiesta';
    this.newDialogTopic = 'COMMERCIALISTA';

    // Open the dialog using the template reference
    const dialogRef = this.dialog.open(this.newConversationDialog, {
      width: '90%',
      maxWidth: '400px',
      autoFocus: 'first-tabbable',
    });

    // We now expect an object with both properties
    dialogRef
      .afterClosed()
      .subscribe((result: { subject: string; topic: any } | undefined) => {
        // If they clicked "Annulla" or the subject is empty, stop
        if (!result || !result.subject?.trim()) {
          return;
        }

        this.loading = true;

        const now = new Date();
        const formattedDate = now.toISOString().split('T')[0];
        const formattedTime = now.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

        // Use the returned topic and subject
        const newConversationData: Partial<AssistanceConversationsInterface> = {
          subject: result.subject.trim(),
          topic: result.topic, // Extracted from the dialog result
          date_start: formattedDate,
          date_end: formattedDate,
          hour_start: formattedTime,
          is_closed: false,
          organization_id: organization_id,
        };

        this.conversationApiService
          .createConversations(organization_id, newConversationData)
          .subscribe({
            next: (newConversation) => {
              this.conversations.unshift(newConversation);
              this.selectConversation(newConversation);
              this.loading = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Error creating conversation:', err);
              this.showErrorMessage(
                'Errore durante la creazione della conversazione',
              );
              this.loading = false;
              this.cdr.detectChanges();
            },
          });
      });

    this.cdr.detectChanges();
  }

  sendMessage(): void {
    // Prevent sending if no text OR no conversation is selected
    if (!this.newMessage.trim() || !this.selectedConversation) return;

    // Count how many 'IN' (received) messages exist in the current chat
    const inMessagesCount = this.tickets.filter(
      (ticket) => ticket.type === 'IN',
    ).length;

    // --- GUARD CLAUSE: Anti-Spam (Last 4 messages) ---
    // Grab up to the last 4 tickets from the array
    const recentTickets = this.tickets.slice(-4);

    // Check if there is at least one admin reply ('IN') in those recent tickets
    const hasAdminReply = recentTickets.some((ticket) => ticket.type === 'IN');

    // If there are 4 recent messages AND none of them are from the admin, block sending
    const isSpamming = recentTickets.length === 4 && !hasAdminReply;
    if (isSpamming) {
      // Show an error and STOP the function immediately
      this.showErrorMessage(
        'Attendi la risposta di un operatore prima di inviare altri messaggi.',
      );
      return;
    }

    // --- CONDITION 2: First Follow-up ---
    // If there is exactly 1 message total (they just opened the chat and are adding a quick detail)
    const isFirstFollowUp = this.tickets.length === 1;
    if (isFirstFollowUp) {
      // Open the confirmation dialog
      const dialogRef = this.dialog.open(this.confirmSendDialog, {
        width: '90%',
        maxWidth: '400px',
      });

      // Wait for the user's choice
      dialogRef.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          this.executeSendMessage(); // They clicked "Invia comunque"
        }
      });
    } else {
      // --- NORMAL SEND ---
      // If there are 0 tickets, OR there are 2+ tickets but the admin HAS replied, send normally
      this.executeSendMessage();
    }

    this.cdr.detectChanges();
  }

  // Moved all your actual sending logic into this private helper function
  private executeSendMessage(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const ticketData: Partial<AssistanceTicketInterface> = {
      message: this.newMessage,
      type: 'OUT',
      organization_id: organization_id,
      conversation: this.selectedConversation!.id,
      title: 'Messaggio Chat',
      date: formattedDate,
      hour: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),

      // evaluates to true if there are 0 tickets, false otherwise
      is_first_message: this.tickets.length === 0,
    };

    this.ticketApiService.createTicket(organization_id, ticketData).subscribe({
      next: (res) => {
        this.tickets = [...this.tickets, res];
        this.newMessage = '';

        if (ticketData.is_first_message === true) {
          // Rinfresca prima di aprire: se la pagina è rimasta aperta a lungo
          // (es. scritto alle 17:59, inviato alle 18:05) il valore caricato in
          // ngOnInit non rifletterebbe più lo stato reale degli uffici.
          this.loadAvailability(() => {
            const dialogRef = this.dialog.open(this.receivedMessageDialog, {
              width: '90%',
              maxWidth: '400px',
            });

            // Wait for the user's choice
            dialogRef.afterClosed();
          });
        }

        this.cdr.detectChanges();
        this.scrollToBottom();
      },
      error: (err) => {
        console.error('Error sending message:', err);
      },
    });

    this.cdr.detectChanges();
  }

  toggleReadStatus(ticket: AssistanceTicketInterface): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.cdr.detectChanges();

    this.ticketApiService
      .updateTicket(organization_id, ticket.id, {
        is_read: true,
      })
      .subscribe({
        next: (updatedTicket: any) => {
          // Update the local data
          const index = this.tickets.findIndex((i) => i.id === ticket.id);

          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('Error updating ticket:', err);
          // Revert the checkbox if the update fails
          ticket.is_read = false;

          this.cdr.detectChanges();
        },
      });
  }

  // Method to force the scrollbar to the bottom
  scrollToBottom(): void {
    // We use setTimeout to let Angular render the new DOM elements first
    setTimeout(() => {
      try {
        if (this.scrollContainer) {
          const container = this.scrollContainer.nativeElement;
          container.scrollTop = container.scrollHeight;

          // this.scrollContainer.nativeElement.scrollTop =
          //   this.scrollContainer.nativeElement.scrollHeight;
        }
      } catch (err) {
        console.error('Scroll error:', err);
      }
    }, 10); // 50ms delay is usually enough for the DOM to update
  }

  handleEnter(event: Event): void {
    // Cast the generic event to a KeyboardEvent
    const keyboardEvent = event as KeyboardEvent;

    // If they press Enter without Shift, send the message
    if (!keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault(); // Prevents the default new line from being added

      if (this.newMessage?.trim()) {
        this.sendMessage();
      }
    }
    // If they press Shift + Enter, let it do its default behavior (new line)
  }

  async bookACall() {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    const dialogRef = this.dialog.open(BookACallDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      height: '750px',
      maxHeight: '90vh',
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      return;
    }
  }

  truncateText(text: string, limit: number = 100): string {
    if (!text) return '';
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
